import React, { useState, useEffect } from "react";
import { Row, Col, Select, Spin, DatePicker, Space } from "antd";
import Chart from "react-apexcharts";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { getAdminAnalytics } from "@api/user/admin";
import {
  TrendingUp,
  TrendingDown,
  Activity,
  Calendar,
  Zap,
  BarChart3,
} from "lucide-react";

const { Option } = Select;
const { RangePicker } = DatePicker;

const UsageOverTime = ({ analytics: initialAnalytics }) => {
  const [dateRange, setDateRange] = useState("30");
  const [customRange, setCustomRange] = useState(null);
  const [loading, setLoading] = useState(false);
  const [analytics, setAnalytics] = useState(initialAnalytics);
  const { t } = useTranslation();

  // Chart configuration
  const [chartData, setChartData] = useState({
    series: [
      {
        name: "Credits Used",
        data: [],
      },
    ],
    options: {
      chart: {
        fontFamily: "Inter, sans-serif",
        type: "area",
        toolbar: { show: false },
        zoom: { enabled: false },
        sparkline: { enabled: false },
        animations: {
          enabled: true,
          easing: "easeinout",
          speed: 800,
        },
      },
      colors: ["#8B5CF6"],
      fill: {
        type: "gradient",
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.45,
          opacityTo: 0.05,
          stops: [0, 100],
          colorStops: [
            { offset: 0, color: "#8B5CF6", opacity: 0.4 },
            { offset: 100, color: "#EC4899", opacity: 0.05 },
          ],
        },
      },
      stroke: {
        curve: "smooth",
        width: 3,
        colors: ["#8B5CF6"],
      },
      dataLabels: { enabled: false },
      grid: {
        borderColor: "#f1f5f9",
        strokeDashArray: 4,
        xaxis: { lines: { show: false } },
        yaxis: { lines: { show: true } },
        padding: { top: 0, right: 0, bottom: 0, left: 10 },
      },
      markers: {
        size: 0,
        colors: ["#8B5CF6"],
        strokeColors: "#fff",
        strokeWidth: 2,
        hover: { size: 6 },
      },
      xaxis: {
        categories: [],
        axisBorder: { show: false },
        axisTicks: { show: false },
        labels: {
          style: {
            colors: "#64748b",
            fontSize: "11px",
            fontWeight: 500,
          },
        },
      },
      yaxis: {
        labels: {
          style: {
            colors: "#64748b",
            fontSize: "11px",
            fontWeight: 500,
          },
          formatter: (value) => {
            if (value >= 1000) {
              return (value / 1000).toFixed(1) + "k";
            }
            return Math.round(value);
          },
        },
        min: 0,
      },
      tooltip: {
        theme: "light",
        x: { show: true },
        y: {
          formatter: (val) => `${val?.toLocaleString() || 0} credits`,
        },
        marker: { show: true },
        style: { fontSize: "12px" },
      },
    },
  });

  // Stats calculations
  const [stats, setStats] = useState({
    total: 0,
    average: 0,
    peak: 0,
    peakDate: "",
    trend: 0,
  });

  // Fetch analytics when date range changes
  useEffect(() => {
    const fetchAnalytics = async () => {
      setLoading(true);
      try {
        const res = await getAdminAnalytics(dateRange);
        setAnalytics(res.data);
      } catch (error) {
        console.error("Error fetching analytics:", error);
      }
      setLoading(false);
    };

    fetchAnalytics();
  }, [dateRange]);

  // Update chart data when analytics changes
  useEffect(() => {
    if (!analytics?.usageOverTime) return;

    let usageData = analytics.usageOverTime || [];

    // Filter data based on date range (client-side fallback)
    if (dateRange !== "all" && dateRange !== "custom") {
      const days = parseInt(dateRange);
      if (!isNaN(days)) {
        const startDate = moment().subtract(days, "days");
        usageData = usageData.filter((item) =>
          moment(item[0]).isAfter(startDate)
        );
      }
    } else if (customRange && customRange[0] && customRange[1]) {
      usageData = usageData.filter((item) => {
        const itemDate = moment(item[0]);
        return itemDate.isBetween(customRange[0], customRange[1], null, "[]");
      });
    }

    // Calculate stats
    const values = usageData.map((item) => item[1] || 0);
    const total = values.reduce((sum, val) => sum + val, 0);
    const average = values.length > 0 ? Math.round(total / values.length) : 0;
    const peak = Math.max(...values, 0);
    const peakIndex = values.indexOf(peak);
    const peakDate =
      usageData[peakIndex] && usageData[peakIndex][0]
        ? moment(usageData[peakIndex][0]).format("MMM DD")
        : "-";

    // Calculate trend (compare first half to second half)
    const midPoint = Math.floor(values.length / 2);
    const firstHalf = values.slice(0, midPoint);
    const secondHalf = values.slice(midPoint);
    const firstHalfAvg =
      firstHalf.length > 0
        ? firstHalf.reduce((a, b) => a + b, 0) / firstHalf.length
        : 0;
    const secondHalfAvg =
      secondHalf.length > 0
        ? secondHalf.reduce((a, b) => a + b, 0) / secondHalf.length
        : 0;
    const trend =
      firstHalfAvg > 0
        ? Math.round(((secondHalfAvg - firstHalfAvg) / firstHalfAvg) * 100)
        : 0;

    setStats({ total, average, peak, peakDate, trend });

    // Update chart
    setChartData((prev) => ({
      ...prev,
      series: [
        {
          name: "Credits Used",
          data: values,
        },
      ],
      options: {
        ...prev.options,
        xaxis: {
          ...prev.options.xaxis,
          categories: usageData.map((item) =>
            moment(item[0]).format("DD MMM")
          ),
        },
        yaxis: {
          ...prev.options.yaxis,
          max: peak > 0 ? Math.ceil(peak * 1.2) : 100,
        },
      },
    }));
  }, [analytics, dateRange, customRange]);

  const dateRangeOptions = [
    { value: "7", label: t("dashboard.last7Days") || "Last 7 days" },
    { value: "14", label: t("dashboard.last14Days") || "Last 14 days" },
    { value: "30", label: t("dashboard.lastMonth") || "Last 30 days" },
    { value: "90", label: t("dashboard.last3Months") || "Last 3 months" },
    { value: "180", label: t("dashboard.last6Months") || "Last 6 months" },
    { value: "365", label: t("dashboard.lastYear") || "Last 1 year" },
    { value: "730", label: t("dashboard.last2Years") || "Last 2 years" },
    { value: "all", label: t("dashboard.allTime") || "All time" },
    { value: "custom", label: t("dashboard.customRange") || "Custom range" },
  ];

  // Handle custom date range change
  const handleCustomRangeChange = (dates) => {
    if (dates && dates[0] && dates[1]) {
      setCustomRange(dates);
      const daysDiff = dates[1].diff(dates[0], "days");
      setDateRange(daysDiff.toString());
    }
  };

  return (
    <div
      style={{
        background: "#fff",
        borderRadius: "16px",
        padding: "24px",
        border: "1px solid #e2e8f0",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: "24px",
          flexWrap: "wrap",
          gap: "16px",
        }}
      >
        <div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              marginBottom: "4px",
            }}
          >
            <div
              style={{
                background: "linear-gradient(135deg, #8B5CF6, #EC4899)",
                borderRadius: "10px",
                padding: "8px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Activity size={18} color="#fff" />
            </div>
            <h3
              style={{
                margin: 0,
                fontSize: "18px",
                fontWeight: 600,
                color: "#1e293b",
              }}
            >
              {t("dashboard.usage") || "Usage Over Time"}
            </h3>
          </div>
          <p
            style={{
              margin: 0,
              fontSize: "13px",
              color: "#64748b",
            }}
          >
            {t("dashboard.usageDesc")}
          </p>
        </div>

        <Space wrap>
          <Select
            value={dateRange === "custom" ? "custom" : dateRangeOptions.find(o => o.value === dateRange)?.value || dateRange}
            onChange={(value) => {
              if (value !== "custom") {
                setCustomRange(null);
              }
              setDateRange(value);
            }}
            style={{ minWidth: "150px" }}
            suffixIcon={<Calendar size={14} color="#64748b" />}
          >
            {dateRangeOptions.map((opt) => (
              <Option key={opt.value} value={opt.value}>
                {opt.label}
              </Option>
            ))}
          </Select>
          {dateRange === "custom" && (
            <RangePicker
              value={customRange}
              onChange={handleCustomRangeChange}
              style={{ minWidth: "240px" }}
              allowClear={false}
            />
          )}
        </Space>
      </div>

      {/* Stats Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: "24px" }}>
        <Col xs={12} sm={6}>
          <div
            style={{
              background: "linear-gradient(135deg, #f5f3ff 0%, #fdf2f8 100%)",
              borderRadius: "12px",
              padding: "16px",
              textAlign: "center",
              minHeight: "100px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "6px",
                marginBottom: "8px",
              }}
            >
              <Zap size={14} color="#8B5CF6" />
              <span
                style={{ fontSize: "11px", color: "#64748b", fontWeight: 500 }}
              >
                {t("dashboard.totalUsed")}
              </span>
            </div>
            <div
              style={{ fontSize: "22px", fontWeight: 700, color: "#8B5CF6" }}
            >
              {stats.total?.toLocaleString()}
            </div>
          </div>
        </Col>

        <Col xs={12} sm={6}>
          <div
            style={{
              background: "linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%)",
              borderRadius: "12px",
              padding: "16px",
              textAlign: "center",
              minHeight: "100px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "6px",
                marginBottom: "8px",
              }}
            >
              <BarChart3 size={14} color="#10B981" />
              <span
                style={{ fontSize: "11px", color: "#64748b", fontWeight: 500 }}
              >
                {t("dashboard.dailyAvg")}
              </span>
            </div>
            <div
              style={{ fontSize: "22px", fontWeight: 700, color: "#10B981" }}
            >
              {stats.average?.toLocaleString()}
            </div>
          </div>
        </Col>

        <Col xs={12} sm={6}>
          <div
            style={{
              background: "linear-gradient(135deg, #fef3c7 0%, #fef9c3 100%)",
              borderRadius: "12px",
              padding: "16px",
              textAlign: "center",
              minHeight: "100px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "6px",
                marginBottom: "4px",
              }}
            >
              <TrendingUp size={14} color="#F59E0B" />
              <span
                style={{ fontSize: "11px", color: "#64748b", fontWeight: 500 }}
              >
                {t("dashboard.peakDay")}
              </span>
            </div>
            <div
              style={{ fontSize: "22px", fontWeight: 700, color: "#F59E0B", lineHeight: 1 }}
            >
              {stats.peak?.toLocaleString()}
            </div>
            <div style={{ fontSize: "10px", color: "#64748b", marginTop: "2px" }}>
              {stats.peakDate}
            </div>
          </div>
        </Col>

        <Col xs={12} sm={6}>
          <div
            style={{
              background:
                stats.trend >= 0
                  ? "linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)"
                  : "linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)",
              borderRadius: "12px",
              padding: "16px",
              textAlign: "center",
              minHeight: "100px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "6px",
                marginBottom: "8px",
              }}
            >
              {stats.trend >= 0 ? (
                <TrendingUp size={14} color="#10B981" />
              ) : (
                <TrendingDown size={14} color="#EF4444" />
              )}
              <span
                style={{ fontSize: "11px", color: "#64748b", fontWeight: 500 }}
              >
                {t("dashboard.trend")}
              </span>
            </div>
            <div
              style={{
                fontSize: "22px",
                fontWeight: 700,
                color: stats.trend >= 0 ? "#10B981" : "#EF4444",
              }}
            >
              {stats.trend >= 0 ? "+" : ""}
              {stats.trend}%
            </div>
          </div>
        </Col>
      </Row>

      {/* Chart */}
      <div style={{ position: "relative", minHeight: "300px" }}>
        {loading && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(255,255,255,0.8)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 10,
              borderRadius: "12px",
            }}
          >
            <Spin size="large" />
          </div>
        )}
        <Chart
          options={chartData.options}
          series={chartData.series}
          type="area"
          height={300}
        />
      </div>
    </div>
  );
};

export default UsageOverTime;
